'use client';

import { useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { usePerformanceLabsIndia } from '../buy/india/PerformanceLabsIndiaContext';
import type { PerformanceLabPlan } from '../buy/india/types';
import { getActivePlanOffer } from '../buy/india/planOffer';
import {
  getCataloguePriceCutOffer,
  getGiftOnlyPriceCutOffer,
  isLabPriceCutWindow,
  LAB_PRICE_CUT_DISCOUNT_CODE,
} from '../buy/india/priceCut';
import { usePerformanceLabAffiliateDiscount } from 'components/performance-lab/hooks';
import {
  FESTIVE_PANELS,
  pricePlanLikeBuyPage,
  type FestiveCardDef,
  type FestivePanelDef,
  type FestivePanelKey,
  type FestiveSubPlanDef,
} from './festiveOffer';

export type FestiveSubPlanItem = {
  id: string;
  name: string;
  validity: string;
  giftLine: string;
  price: number;
  strikePrice?: number;
  perSessionPrice?: number;
  recommended?: boolean;
  /** True only while the Lab festive cut (EXINPL15) is the active price for this line. */
  priceCut: boolean;
  discountCode?: string;
  shopifyVariantId: string;
  quantity: number;
};

export type FestiveCardItem = {
  id: string;
  panel: FestivePanelKey;
  name: string;
  description: string;
  spec: string;
  helper: string;
  category: string;
  chips: string[];
  includes: string[];
  subPlans: FestiveSubPlanItem[];
  /** Card price. Groups show the first sub-plan, which is the single session. */
  price: number;
  strikePrice?: number;
  multiple: boolean;
  detailPlan?: PerformanceLabPlan;
};

export type FestivePanelItems = {
  def: FestivePanelDef;
  cards: FestiveCardItem[];
};

export const formatInr = (value: number) => `₹${value.toLocaleString('en-IN')}`;

const textOf = (t: (key: string) => string, key: string) => (key ? t(key) : '');

const resolveSubPlan = (
  def: FestiveSubPlanDef,
  plansById: Map<string, PerformanceLabPlan>,
  t: (key: string) => string,
  affiliateFor: (variantId?: string) => {
    percentage: number;
    discountCode: string;
  },
): FestiveSubPlanItem | null => {
  const quantity = def.quantity ?? 1;
  const name = textOf(t, def.nameKey);
  const validity = textOf(t, def.validityKey);
  const giftLine = textOf(t, def.giftLineKey);

  if (def.catalogueId) {
    const plan = plansById.get(def.catalogueId);
    if (!plan?.shopifyVariantId) return null;
    const pricing = pricePlanLikeBuyPage(
      plan,
      affiliateFor(plan.shopifyVariantId),
    );
    const cut = getCataloguePriceCutOffer(plan.id);
    const active = getActivePlanOffer(plan);
    const priceCut = Boolean(
      cut &&
        active &&
        active.offerPrice === cut.offerPrice &&
        pricing.discountCode === LAB_PRICE_CUT_DISCOUNT_CODE,
    );
    return {
      id: def.id,
      name,
      validity,
      giftLine,
      price: pricing.price * quantity,
      strikePrice: pricing.strikePrice
        ? pricing.strikePrice * quantity
        : undefined,
      perSessionPrice: quantity > 1 ? pricing.price : undefined,
      recommended: def.recommended,
      priceCut,
      discountCode: pricing.discountCode,
      shopifyVariantId: plan.shopifyVariantId,
      quantity,
    };
  }

  if (!def.shopifyVariantId || def.price == null) return null;

  const affiliate = affiliateFor(def.shopifyVariantId);
  const giftCut = getGiftOnlyPriceCutOffer(def.id);
  const inWindow = isLabPriceCutWindow();

  if (affiliate.percentage > 0 && affiliate.discountCode && def.price > 0) {
    return {
      id: def.id,
      name,
      validity,
      giftLine,
      price: Math.round(def.price * (1 - affiliate.percentage / 100)),
      strikePrice: def.price,
      perSessionPrice: def.perSessionPrice,
      recommended: def.recommended,
      priceCut: false,
      discountCode: affiliate.discountCode,
      shopifyVariantId: def.shopifyVariantId,
      quantity,
    };
  }

  if (giftCut && inWindow && def.price > 0) {
    return {
      id: def.id,
      name,
      validity,
      giftLine,
      price: giftCut.offerPrice,
      strikePrice: giftCut.strikePrice,
      perSessionPrice:
        def.perSessionPrice != null
          ? Math.round(def.perSessionPrice * 0.85)
          : undefined,
      recommended: def.recommended,
      priceCut: true,
      discountCode: giftCut.discountCode,
      shopifyVariantId: def.shopifyVariantId,
      quantity,
    };
  }

  return {
    id: def.id,
    name,
    validity,
    giftLine,
    price: def.price,
    strikePrice: def.strikePrice,
    perSessionPrice: def.perSessionPrice,
    recommended: def.recommended,
    priceCut: false,
    shopifyVariantId: def.shopifyVariantId,
    quantity,
  };
};

const resolveCard = (
  def: FestiveCardDef,
  panel: FestivePanelKey,
  plansById: Map<string, PerformanceLabPlan>,
  t: (key: string) => string,
  tPlans: (key: string) => string,
  affiliateFor: (variantId?: string) => {
    percentage: number;
    discountCode: string;
  },
): FestiveCardItem | null => {
  const subPlans = def.subPlans
    .map((entry) => resolveSubPlan(entry, plansById, t, affiliateFor))
    .filter((entry): entry is FestiveSubPlanItem => entry != null);
  if (!subPlans.length) return null;

  const leadSub = def.subPlans[0];
  const catalogueId =
    leadSub?.catalogueId ??
    (def.id === 'recovery-membership-monthly'
      ? 'recovery-month-pass'
      : def.id === 'recovery-membership-annual'
      ? 'recovery-3-month-pass'
      : undefined);
  const detailPlan = catalogueId ? plansById.get(catalogueId) : undefined;
  const description = def.descriptionKey
    ? textOf(t, def.descriptionKey)
    : detailPlan
    ? tPlans(detailPlan.description)
    : '';
  const lead = subPlans[0];

  return {
    id: def.id,
    panel,
    name: textOf(t, def.nameKey),
    description,
    spec: textOf(t, def.specKey),
    helper: textOf(t, def.helperKey),
    category: textOf(t, def.categoryKey),
    chips: (def.chipKeys ?? []).map((key) => textOf(t, key)),
    includes: (def.includeKeys ?? []).map((key) => textOf(t, key)),
    subPlans,
    price: lead.price,
    strikePrice: lead.strikePrice,
    multiple: subPlans.length > 1,
    detailPlan,
  };
};

/** Festive cards, with shared plans priced the same way as the buy page. */
export const useFestivePlanItems = (): FestivePanelItems[] => {
  const t = useTranslations('performance-lab');
  const tPlans = useTranslations('diagnostics');
  const { sections } = usePerformanceLabsIndia();
  const { getPlanDiscount } = usePerformanceLabAffiliateDiscount();

  return useMemo(() => {
    const plansById = new Map<string, PerformanceLabPlan>();
    sections.forEach((section) => {
      section.plans.forEach((plan) => plansById.set(plan.id, plan));
      section.subcategories?.forEach((subcategory) =>
        subcategory.plans.forEach((plan) => plansById.set(plan.id, plan)),
      );
    });
    const affiliateFor = (variantId?: string) => getPlanDiscount(variantId);

    return FESTIVE_PANELS.map((panel) => ({
      def: panel,
      cards: panel.cards
        .map((entry) =>
          resolveCard(entry, panel.key, plansById, t, tPlans, affiliateFor),
        )
        .filter((entry): entry is FestiveCardItem => entry != null),
    }));
  }, [getPlanDiscount, sections, t, tPlans]);
};

export const useFestiveCard = (cardId: string): FestiveCardItem | null => {
  const panels = useFestivePlanItems();
  return (
    panels.flatMap((panel) => panel.cards).find((card) => card.id === cardId) ??
    null
  );
};
